import { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Button from '@liquidweb-dev/undercurrent/Button';
import KeyItem from '@liquidweb-dev/undercurrent/KeyItem';
import MeasureBar from '@liquidweb-dev/undercurrent/MeasureBar';
import Trash2Icon from '@liquidweb-dev/undercurrent/icons/lucide/Trash2';
import toast from '@liquidweb-dev/undercurrent/toast';
import useProductFlatDetails from 'modules/queries/product/useFlatDetails';
import useAssetUpdate from 'modules/queries/asset/useUpdate';
import useCloudBackupList from 'modules/queries/cloud/backup/useList';
import QuotaSelect from 'containers/pages/servers/BackupsAndImages/BackupsBlock/Settings/QuotaSelect';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import CloudBackupSettingsContent from 'containers/pages/servers/BackupsAndImages/BackupsBlock/Settings/Cloud/CloudBackupSettingsContent';
import { BackupOption } from 'modules/backups/helpers';
import { useAssetBackupsUpsell } from 'modules/upsell/useUpsellState';
import { useBackupsDrawerContext } from 'components/standard/ServersList/BackupsChip/BackupsDrawerContext';

interface CloudQuotaBackupSettingsProps {
	assetDetails: AssetDetails;
	backupType: string;
	onBackupTypeChange: (value: string) => void;
	backupOptions?: BackupOption[];
	onDestroy: () => void;
	onClose: () => void;
}

const CloudQuotaBackupSettings = ({
	assetDetails,
	backupType,
	onBackupTypeChange,
	backupOptions,
	onDestroy,
	onClose,
}: CloudQuotaBackupSettingsProps) => {
	const queryClient = useQueryClient();

	const [quotaOption, setQuotaOption] = useState<number | undefined>();

	const { data: productDetails, isLoading: isProductDetailsLoading } =
		useProductFlatDetails(
			{ product_code: assetDetails?.type },
			{ enabled: Boolean(assetDetails?.type) },
		);

	const { mutate: updateAsset, isLoading: isUpdateAssetLoading } =
		useAssetUpdate();

	const { data: stormBackups, isLoading: isStormBackupsLoading } =
		useCloudBackupList({ uniq_id: assetDetails.uniq_id, page_size: 1 });

	const [_, setUpsellDismissed] = useAssetBackupsUpsell(assetDetails.uniq_id);
	const { onSuccess } = useBackupsDrawerContext();

	const destinationPtovId = useMemo(() => {
		if (assetDetails.backupPlan === 'Quota') {
			return assetDetails?.featureDetails?.LiquidWebBackupPlan?.ptov_id;
		}

		return productDetails?.flat_options?.find(
			(item) => item?.key === 'BackupQuota',
		)?.parent_ptov_id;
	}, [assetDetails, productDetails]);

	const currentQuota = useMemo(
		() =>
			Object.values(assetDetails?.featureDetails || {})?.find(
				(item) => item?.parent_ptov_id === destinationPtovId,
			),
		[assetDetails, destinationPtovId],
	);

	const quotaOptions = useMemo(
		() =>
			productDetails?.flat_options
				?.find((item) => item?.parent_ptov_id === destinationPtovId)
				?.values?.filter((item) => item.public)
				?.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)) || [],
		[productDetails, destinationPtovId],
	);

	useEffect(() => {
		if (!quotaOption && quotaOptions.length > 0) {
			setQuotaOption(currentQuota?.ptov_id ?? quotaOptions[0]?.ptov_id);
		}
	}, [currentQuota, quotaOption, quotaOptions]);

	const quotaOptionValue = useMemo(
		() =>
			quotaOptions.find((item) => item.ptov_id === Number(quotaOption))?.value,
		[quotaOption, quotaOptions],
	);

	const percentage = useMemo(() => {
		const selected = Number(quotaOptionValue || '0');
		const max = Number(quotaOptions[quotaOptions.length - 1]?.value || '1');
		return (selected / max) * 100;
	}, [quotaOptionValue, quotaOptions]);

	const handleQuotaOptionChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setQuotaOption(Number(event.target.value));
	};

	const handleSubmit = () => {
		const key = assetDetails?.featureDetails
			? Object.entries(assetDetails?.featureDetails)?.find(
					([, value]) => value?.parent_ptov_id === destinationPtovId,
				)?.[0] || 'BackupQuota'
			: 'BackupQuota';

		updateAsset(
			{
				uniq_id: assetDetails?.uniq_id,
				features: { LiquidWebBackupPlan: 'Quota', [key]: quotaOptionValue },
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: ['/acronis/backup/usages'],
					});

					toast.info('Successfully submitted');
					setUpsellDismissed(true);
					onClose();
					onSuccess?.();
				},
			},
		);
	};

	return (
		<CloudBackupSettingsContent
			backupType={backupType}
			onBackupTypeChange={onBackupTypeChange}
			isLoading={
				isProductDetailsLoading || isUpdateAssetLoading || isStormBackupsLoading
			}
			backupOptions={backupOptions}
			onClose={onClose}
			footerContents={
				<>
					<Button
						onClick={handleSubmit}
						loading={isUpdateAssetLoading}
						disabled={
							isProductDetailsLoading || quotaOption === currentQuota?.ptov_id
						}
						size="large"
						color={currentQuota ? 'primary' : 'highlight'}
					>
						{currentQuota ? 'Update' : 'Buy now'}
					</Button>
					{currentQuota ? (
						<Button
							variant="secondary"
							color="danger"
							disabled={
								isProductDetailsLoading ||
								isUpdateAssetLoading ||
								isStormBackupsLoading
							}
							size="large"
							startIcon={
								<Trash2Icon
									sx={({ size }) => ({ width: size.xS, height: size.xS })}
								/>
							}
							sx={{ paddingY: 100, paddingX: 200, alignSelf: 'stretch' }}
							onClick={onDestroy}
						>
							Remove backups
						</Button>
					) : (
						<Button
							variant="ghost"
							onClick={onClose}
							disabled={isUpdateAssetLoading}
							size="large"
						>
							Cancel
						</Button>
					)}
				</>
			}
		>
			{currentQuota && (
				<Stack direction="row" spacing={100}>
					{(stormBackups?.item_total || 0) >= 0 && (
						<KeyItem
							label="Backup Count"
							value={
								isStormBackupsLoading ? (
									<Skeleton width={100} />
								) : (
									stormBackups?.item_total
								)
							}
							sx={{ flex: 1 }}
						/>
					)}
					<KeyItem
						label="Current space used"
						value={`${parseFloat(assetDetails?.instance?.backup_size || '0')} GB`}
						sx={{ flex: 1 }}
					/>
				</Stack>
			)}
			<Stack direction="row" spacing={200}>
				<MeasureBar
					orientation="vertical"
					percentage={percentage}
					height={1}
					minWidth={72}
					hidePercentage
				/>
				{productDetails && (
					<QuotaSelect
						acronisProductDetails={productDetails}
						assetDetails={assetDetails}
						destination={Number(destinationPtovId ?? '0')}
						disabled={isUpdateAssetLoading}
						currentQuota={Number(currentQuota?.value ?? '0')}
						onChange={handleQuotaOptionChange}
						value={Number(quotaOption ?? '0')}
					/>
				)}
			</Stack>
		</CloudBackupSettingsContent>
	);
};

export default CloudQuotaBackupSettings;
